import Image from 'app/Components/Shared/Image/image';
import React, {useState, useMemo, useEffect} from 'react';
import FilterBar from './FilterBar';
import Pagination from './Pagination';
import BtnBlackBorder from 'app/Components/Shared/Buttons/BtnWhiteBlackBorderSmallSize';
import {upload} from './Icon/svgIcons';
import Papa from 'papaparse';
import {saveAs} from 'file-saver';
import {isMobile} from 'react-device-detect';

import './FlexibleTable.scss';

const FlexibleTable = ({
  columns,
  data,
  onSort,
  pagination = false,
  pageSize = 10,
  onLoadMore = null,
  hasMoreData = false,
  showDownloadButton = false,
  getCSVData = null,
  filterOptions,
  downloadFileTitle = 'קובץ_נתונים',
}) => {
  const totalCount = data.length;
  const tableColumns = columns.tableColumns;
  const collapsedRow = columns?.collapsedRow;
  const [sortConfig, setSortConfig] = useState({key: null, direction: 'asc'});
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filteredData, setFilteredData] = useState(data);
  const [sortedData, setSortedData] = useState(data);

  // Reset sorted and filtered data when original data changes
  useEffect(() => {
    setSortedData(data);
    setFilteredData(data);
    setSortConfig({key: null, direction: 'asc'});
    setCurrentPage(1); // Reset to first page when data changes
  }, [data]);

  // Use sorted data, then filtered data, or original data
  const dataToUse = !filterOptions.onlineFilter ? sortedData : filteredData;

  const paginatedData = useMemo(() => {
    if (!pagination) return dataToUse;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return dataToUse.slice(startIndex, endIndex);
  }, [dataToUse, currentPage, pageSize, pagination]);

  const handleSort = (columnKey) => {
    const column = tableColumns?.find((col) => col.key === columnKey);
    if (!column?.sortable) return;

    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({key: columnKey, direction});

    // Sort the data based on the current data source
    const dataSource = !filterOptions.onlineFilter ? data : filteredData;
    const sortedDataResult = [...dataSource].sort((a, b) => {
      const aValue = a[columnKey];
      const bValue = b[columnKey];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return direction === 'asc' ? 1 : -1;
      if (bValue == null) return direction === 'asc' ? -1 : 1;

      // Determine data type and sort accordingly
      const result = compareValues(aValue, bValue, column.type);

      return direction === 'asc' ? result : -result;
    });

    // Update the appropriate data state
    if (!filterOptions.onlineFilter) {
      setSortedData(sortedDataResult);
    } else {
      setFilteredData(sortedDataResult);
    }

    // Call the external onSort callback if provided
    if (onSort) {
      onSort(columnKey, direction, sortedDataResult);
    }
  };

  // Helper function to compare different data types
  const compareValues = (a, b, columnType) => {
    switch (columnType) {
      case 'number':
        return Number(a) - Number(b);

      case 'date':
        return new Date(a) - new Date(b);

      case 'text':
      default:
        // Convert to string and compare (case-insensitive for Hebrew/English)
        const aStr = String(a).toLowerCase();
        const bStr = String(b).toLowerCase();

        if (aStr < bStr) return -1;
        if (aStr > bStr) return 1;
        return 0;
    }
  };
  // Handle filter changes to do in the future
  // const handleFilterChange = (filterKey, filterValue, allFilters) => {
  //   if (filterOptions.onFilterChange) {
  //     filterOptions.onFilterChange(filterKey, filterValue, allFilters);
  //   }

  // Apply local filtering if no external filter handler, to do

  // if (filterOptions.tableFilters.onlineFilter && !filterOptions.onFilterChange && filterOptions.tableFilters) {
  //   const filtered = data.filter((item) => {
  //     return Object.keys(allFilters).every((key) => {
  //       const filter = filterOptions.tableFilters.find((f) => f.key === key);
  //       const value = allFilters[key];

  //       if (!value || value === '' || (Array.isArray(value) && value.length === 0)) {
  //         return true;
  //       }

  //       const itemValue = item[key];

  //       switch (filter?.type) {
  //         case 'text':
  //           return itemValue?.toString().toLowerCase().includes(value.toLowerCase());
  //         case 'select':
  //           return itemValue === value;
  //         case 'multiselect':
  //           return Array.isArray(value) ? value.includes(itemValue) : true;
  //         case 'date':
  //           return itemValue === value;
  //         case 'daterange':
  //           const itemDate = new Date(itemValue);
  //           const fromDate = value.startDate ? new Date(value.startDate) : null;
  //           const toDate = value.endDate ? new Date(value.endDate) : null;

  //           if (fromDate && itemDate < fromDate) return false;
  //           if (toDate && itemDate > toDate) return false;
  //           return true;
  //         case 'number':
  //           return itemValue?.toString() === value.toString();
  //         case 'checkbox':
  //           return value ? itemValue === true : true;
  //         default:
  //           return true;
  //       }
  //     });
  //   });

  //   setFilteredData(filtered);
  //   setCurrentPage(1); // Reset to first page when filtering
  // }
  //};

  const handleClearFilters = () => {
    setFilteredData(data);
    setSortedData(data);
    setSortConfig({key: null, direction: 'asc'});
    setCurrentPage(1);
    if (filterOptions.onFilterChange) {
      filterOptions.onFilterChange(null, null, {});
    }
  };

  const downloadCSV = () => {
    const formattedData = getCSVData();
    // Convert JSON to CSV
    const csv = Papa.unparse(formattedData);
    const csvWithBOM = '\uFEFF' + csv;
    const blob = new Blob([csvWithBOM], {type: 'text/csv;charset=utf-8;'});
    saveAs(blob, `${downloadFileTitle}.csv`);
  };

  const renderCellContent = (item, column) => {
    const value = item[column.key];

    switch (column.type) {
      case 'button':
        return (
          <button
            className={`btn ${column.className || 'btn-primary'}`}
            onClick={() => column.onClick && column.onClick(item)}
            disabled={column.disabled && column.disabled(item)}>
            {column.icon && <i className={column.icon} />}
            {column.label || value}
          </button>
        );

      case 'link':
        return (
          <a
            href={column.href ? column.href(item) : '#'}
            className={column.className || 'link'}
            onClick={(e) => {
              if (column.onClick) {
                e.preventDefault();
                column.onClick(item);
              }
            }}>
            {value}
          </a>
        );
      case 'badge':
        return <span className={`badge ${column.className ? column.getBadgeClass(value) : 'badge-secondary'}`}>{value}</span>;
      case 'custom':
        return column.render ? (
          <span className={column.className || ''}>{column.render(value, item)}</span>
        ) : (
          <span className={column.className || ''}>{value}</span>
        );

      default:
        return <span className={column.className || ''}>{value}</span>;
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      // Return the default sorting icon (both up and down arrows)
      return (
        <div className='sort-icon-container'>
          <Image src={require('./Icon/arrows.svg')} alt='sort' className='sort-icon' />
        </div>
      );
    }

    // Return specific direction icon based on current sort
    return (
      <div className='sort-icon-container'>
        <Image src={require('./Icon/sorting.svg')} alt='sort ascending' className={`sort-icon sort-${sortConfig.direction}`} />
      </div>
    );
  };

  const handleCollapse = (rowId) => {
    setExpandedRows((prev) => {
      const newExpandedRows = new Set();
      if (!prev.has(rowId)) {
        newExpandedRows.add(rowId);
      }
      return newExpandedRows;
    });
  };

  const isRowExpanded = (rowId) => {
    return expandedRows.has(rowId);
  };

  const getCollapsedColumn = () => {
    return tableColumns.find((column) => column.key === 'collapsed');
  };

  return (
    <div className='flexible-table-wrapper'>
      {filterOptions.showFilterBar && filterOptions.tableFilters && (
        <FilterBar
          filterOptions={filterOptions}
          //onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          getCSVData={getCSVData}
        />
      )}

      <div className='flexible-table-container'>
        <table className='table table-hover'>
          <thead>
            <tr>
              {tableColumns.map(
                (column, index) =>
                  column.key != 'collapsed' && (
                    <th
                      key={index}
                      className={`${column.sortable ? 'sortable' : ''} ${column.align ? `text-${column.align}` : ''}`}
                      style={{width: column.width}}
                      onClick={() => handleSort(column.key)}>
                      <div className='th-content'>
                        <div className='column-title'>{column.title}</div>
                        {column.sortable && getSortIcon(column.key)}
                      </div>
                    </th>
                  ),
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, rowIndex) => {
              const rowId = item.id || rowIndex;
              const isExpanded = isRowExpanded(rowId);
              const collapsedColumn = getCollapsedColumn();

              return (
                <React.Fragment key={rowId}>
                  <tr className='fu-table-row'>
                    {tableColumns.map((column, colIndex) =>
                      column.key != 'collapsed' ? (
                        <td key={colIndex} className={column.align ? `text-${column.align}` : ''} style={{width: column.width}}>
                          {renderCellContent(item, column)}
                          {collapsedRow && colIndex == tableColumns.filter((col) => col.key !== 'collapsed').length - 1 && (
                            <div type='button' className='collapse-icon' onClick={() => handleCollapse(rowId)}>
                              <Image src={require('./Icon/arrow-collapsed.svg')}
                                alt='collapse'
                                style={{
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.3s ease',
                                }} />
                            </div>
                          )}
                        </td>
                      ) : null,
                    )}
                  </tr>
                  {/* Render collapsed content if row is expanded */}
                  {collapsedRow && isExpanded && collapsedColumn && (
                    <tr className='collapsed-row'>
                      <td colSpan={tableColumns.filter((col) => col.key !== 'collapsed').length} className='collapsed-content'>
                        {collapsedColumn.render ? collapsedColumn.render(item, item) : null}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <div className='flexible-table-footer'>
          {showDownloadButton && (
            <div className='download-csv-btn'>
              <BtnBlackBorder svgImage={upload} text='ייצא לטבלת CSV' onclick={() => downloadCSV()} />
            </div>
          )}
          {pagination && (
            <Pagination
              totalCount={totalCount}
              pagination={pagination}
              pageSize={pageSize}
              currentPage={currentPage}
              dataToUse={dataToUse}
              onPageChange={setCurrentPage}
              hasMoreData={hasMoreData}
              onLoadMore={onLoadMore}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FlexibleTable;
